import { useCallback, useEffect, useRef, useState } from 'react';
import { socket } from '../../config/SocketApi.js';
import { SOCKET_EVENTS } from '../../config/socketEvents.js';
import { deriveLessonPacing } from './lesson-pacing.js';

export function useLessonPacing(roomId, { enabled = true, startOnMount = false, isTutor = false } = {}) {
    const [snapshot, setSnapshot] = useState(null);
    const [view, setView] = useState(null);
    const receivedAtRef = useRef(0);

    const accept = useCallback((response) => {
        if (response?.status && response.status !== 'success') return;
        const next = response?.status === 'success' ? response.data : response;
        if (!next) {
            setSnapshot(null);
            setView(null);
            return;
        }
        if (String(next.room_id) !== String(roomId)
            || !Number.isFinite(Number(next.server_now_ms))
            || !Number.isFinite(Number(next.duration_seconds))) return;
        receivedAtRef.current = window.performance.now();
        setSnapshot(next);
        setView(deriveLessonPacing(next));
    }, [roomId]);

    const request = useCallback(() => {
        socket.emit(SOCKET_EVENTS.GET_LESSON_PACING, { room_id: String(roomId) }, accept);
    }, [accept, roomId]);
    const stateEvent = isTutor ? SOCKET_EVENTS.LESSON_PACING_TEACHER_STATE : SOCKET_EVENTS.LESSON_PACING_STATE;

    const update = useCallback((patch) => new Promise((resolve) => {
        socket.emit(SOCKET_EVENTS.UPDATE_LESSON_PACING, patch, (response) => {
            if (response?.status === 'success') accept(response);
            resolve(response || { status: 'error', code: 'no_acknowledgement' });
        });
    }), [accept]);

    useEffect(() => {
        receivedAtRef.current = 0;
        setSnapshot(null);
        setView(null);
    }, [roomId]);

    useEffect(() => {
        if (!enabled || !roomId) return undefined;
        socket.on(stateEvent, accept);
        socket.on('connect', request);
        request();
        if (startOnMount) {
            socket.emit('teacher-started-session', { room_id: String(roomId) }, accept);
        }
        return () => {
            socket.off(stateEvent, accept);
            socket.off('connect', request);
        };
    }, [accept, enabled, request, roomId, startOnMount, stateEvent]);

    useEffect(() => {
        if (!snapshot) {
            setView(null);
            return undefined;
        }
        const updateView = () => {
            setView(deriveLessonPacing(snapshot, window.performance.now() - receivedAtRef.current));
        };
        updateView();
        const interval = window.setInterval(updateView, 1000);
        return () => window.clearInterval(interval);
    }, [snapshot]);

    return { snapshot, view, refresh: request, update };
}
